import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, Users, Briefcase, ChevronUp, ChevronDown } from 'lucide-react';
import { Director, LoanOfficer, Loan } from '../types';
import { formatCurrency, formatNumber } from '../utils/formatters';
interface DirectorDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  director: Director | null;
}
type LOSortField = 'name' | 'loansClosed' | 'loanVolume' | 'avgRevenue';
type LoanSortField = 'borrower' | 'product' | 'closeDate' | 'amount';
type SortDir = 'asc' | 'desc';
function SortHeader({
  label,
  field,
  activeField,
  direction,
  onSort,
  align = 'left'
}: {
  label: string;
  field: string;
  activeField: string;
  direction: SortDir;
  onSort: (field: any) => void;
  align?: 'left' | 'right';
}) {
  const isActive = field === activeField;
  return <button onClick={e => {
    e.stopPropagation();
    onSort(field);
  }} className={`group inline-flex items-center gap-1 text-xs uppercase ${isActive ? 'text-slate-200' : 'text-slate-500 hover:text-slate-300'} transition-colors ${align === 'right' ? 'justify-end w-full' : ''}`} data-id="element-194">
      <span data-id="element-195">{label}</span>
      {isActive ? direction === 'asc' ? <ChevronUp className="w-3 h-3 text-indigo-400" data-id="element-196" /> : <ChevronDown className="w-3 h-3 text-indigo-400" data-id="element-197" /> : <ChevronDown className="w-3 h-3 opacity-0 group-hover:opacity-40 transition-opacity" data-id="element-198" />}
    </button>;
}
function compare(a: any, b: any, dir: SortDir) {
  if (typeof a === 'string' && typeof b === 'string') {
    return dir === 'asc' ? a.localeCompare(b) : b.localeCompare(a);
  }
  return dir === 'asc' ? a - b : b - a;
}
function LoansTable({
  loans
}: {
  loans: Loan[];
}) {
  const [sortField, setSortField] = useState<LoanSortField>('amount');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const handleSort = (field: LoanSortField) => {
    if (field === sortField) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');else {
      setSortField(field);
      setSortDir('desc');
    }
  };
  const sorted = useMemo(() => [...loans].sort((a, b) => compare(a[sortField], b[sortField], sortDir)), [loans, sortField, sortDir]);
  return <div className="rounded-lg overflow-hidden bg-black/30 border border-white/5" data-id="element-199">
      <table className="w-full text-left" data-id="element-200">
        <thead data-id="element-201">
          <tr className="border-b border-white/5" data-id="element-202">
            <th className="py-2 px-3" data-id="element-203">
              <SortHeader label="Borrower" field="borrower" activeField={sortField} direction={sortDir} onSort={handleSort} data-id="element-204" />
            </th>
            <th className="py-2 px-3" data-id="element-205">
              <SortHeader label="Product" field="product" activeField={sortField} direction={sortDir} onSort={handleSort} data-id="element-206" />
            </th>
            <th className="py-2 px-3" data-id="element-207">
              <SortHeader label="Date" field="closeDate" activeField={sortField} direction={sortDir} onSort={handleSort} data-id="element-208" />
            </th>
            <th className="py-2 px-3 text-right" data-id="element-209">
              <SortHeader label="Amount" field="amount" activeField={sortField} direction={sortDir} onSort={handleSort} align="right" data-id="element-210" />
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5" data-id="element-211">
          {sorted.map(loan => <tr key={loan.id} data-id="element-212">
              <td className="py-2 px-3 text-sm text-white" data-id="element-213">{loan.borrower}</td>
              <td className="py-2 px-3 text-xs text-slate-400" data-id="element-214">
                {loan.product}
              </td>
              <td className="py-2 px-3 text-xs text-slate-400" data-id="element-215">
                {loan.closeDate}
              </td>
              <td className="py-2 px-3 text-sm text-white text-right" data-id="element-216">
                {formatCurrency(loan.amount)}
              </td>
            </tr>)}
        </tbody>
      </table>
    </div>;
}
function LoanOfficerRow({
  lo
}: {
  lo: LoanOfficer & {
    avgRevenue: number;
  };
}) {
  const [expanded, setExpanded] = useState(false);
  return <div className={`rounded-lg border overflow-hidden transition-colors ${expanded ? 'bg-white/[0.04] border-white/15' : 'bg-white/[0.02] border-white/5 hover:border-white/10'}`} data-id="element-217">
      <button onClick={() => setExpanded(!expanded)} className="w-full grid grid-cols-[20px_1fr_80px_120px_120px] gap-3 items-center px-4 py-2.5 text-left" data-id="element-218">
        <ChevronRight className={`w-3.5 h-3.5 text-slate-500 transition-transform ${expanded ? 'rotate-90' : ''}`} data-id="element-219" />
        <div className="flex items-center gap-3 min-w-0" data-id="element-220">
          <div className="w-7 h-7 rounded-full bg-violet-500/20 border border-violet-500/30 flex items-center justify-center text-violet-200 text-[10px] font-semibold shrink-0" data-id="element-221">
            {lo.initials}
          </div>
          <div className="min-w-0" data-id="element-222">
            <p className="text-sm text-white font-medium truncate" data-id="element-223">{lo.name}</p>
            <p className="text-[10px] text-slate-500 truncate uppercase tracking-wider" data-id="element-224">
              Loan Officer
            </p>
          </div>
        </div>
        <span className="text-sm text-slate-300 text-right" data-id="element-225">
          {formatNumber(lo.loansClosed)}
        </span>
        <span className="text-sm text-white text-right" data-id="element-226">
          {formatCurrency(lo.loanVolume, true)}
        </span>
        <span className="text-sm text-emerald-400 text-right" data-id="element-227">
          {formatCurrency(lo.avgRevenue)}
        </span>
      </button>
      <AnimatePresence initial={false} data-id="element-228">
        {expanded && <motion.div key="content" initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: 'easeOut'
      }} className="overflow-hidden" data-id="element-229">
            <div className="px-4 pb-4 pt-1 border-t border-white/5" data-id="element-230">
              <div className="flex items-center gap-2 text-xs uppercase text-slate-500 mb-2 mt-3" data-id="element-231">
                <Briefcase className="w-3.5 h-3.5" data-id="element-232" /> Contributing Loans
              </div>
              <LoansTable loans={lo.loans} data-id="element-233" />
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function DirectorDetailModal({
  isOpen,
  onClose,
  director
}: DirectorDetailModalProps) {
  const [loSortField, setLoSortField] = useState<LOSortField>('avgRevenue');
  const [loSortDir, setLoSortDir] = useState<SortDir>('desc');
  const enrichedLOs = useMemo(() => (director?.loanOfficers ?? []).map(lo => ({
    ...lo,
    avgRevenue: lo.loansClosed > 0 ? lo.totalRevenue / lo.loansClosed : 0
  })), [director]);
  const sortedLOs = useMemo(() => [...enrichedLOs].sort((a, b) => compare(a[loSortField], b[loSortField], loSortDir)), [enrichedLOs, loSortField, loSortDir]);
  const handleLoSort = (field: LOSortField) => {
    if (field === loSortField) setLoSortDir(loSortDir === 'asc' ? 'desc' : 'asc');else {
      setLoSortField(field);
      setLoSortDir('desc');
    }
  };
  if (!director) return null;
  return <AnimatePresence data-id="element-234">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40" onClick={onClose} data-id="element-235" />
          <div className="fixed inset-0 flex items-start justify-center z-50 p-4 pointer-events-none overflow-y-auto" data-id="element-236">
            <motion.div initial={{
          opacity: 0,
          scale: 0.97,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.97,
          y: 20
        }} className="bg-[#141a2c] rounded-3xl my-6 max-w-5xl w-full shadow-2xl pointer-events-auto border border-white/10 relative overflow-hidden" data-id="element-237">
              {/* Header */}
              <div className="flex items-start justify-between p-6 border-b border-white/5" data-id="element-238">
                <div className="flex items-center gap-4" data-id="element-239">
                  <div className="w-16 h-16 rounded-2xl bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center text-indigo-200 font-bold text-xl" data-id="element-240">
                    {director.initials}
                  </div>
                  <div data-id="element-241">
                    <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 text-[10px] uppercase tracking-wider font-medium mb-1.5" data-id="element-242">
                      <Users className="w-3 h-3" data-id="element-243" /> Director
                    </div>
                    <h2 className="text-2xl font-bold text-white" data-id="element-244">
                      {director.name}
                    </h2>
                    <p className="text-sm text-slate-400" data-id="element-245">
                      {director.avpName ? `Reports to ${director.avpName}` : 'Director'}
                      {director.division ? ` · ${director.division} Division` : ''}
                    </p>
                  </div>
                </div>
                <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors p-1" data-id="element-246">
                  <X className="w-5 h-5" data-id="element-247" />
                </button>
              </div>

              {/* Summary KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-6 border-b border-white/5" data-id="element-248">
                <div className="bg-black/30 rounded-xl p-4 border border-white/5" data-id="element-249">
                  <p className="text-xs text-slate-400 mb-1" data-id="element-250">Loan Officers</p>
                  <p className="text-lg font-bold text-white" data-id="element-251">
                    {director.loCount}
                  </p>
                </div>
                <div className="bg-black/30 rounded-xl p-4 border border-white/5" data-id="element-252">
                  <p className="text-xs text-slate-400 mb-1" data-id="element-253">Loans Closed</p>
                  <p className="text-lg font-bold text-white" data-id="element-254">
                    {formatNumber(director.loansClosed)}
                  </p>
                </div>
                <div className="bg-black/30 rounded-xl p-4 border border-white/5" data-id="element-255">
                  <p className="text-xs text-slate-400 mb-1" data-id="element-256">Loan Volume</p>
                  <p className="text-lg font-bold text-white" data-id="element-257">
                    {formatCurrency(director.loanVolume, true)}
                  </p>
                </div>
                <div className="bg-black/30 rounded-xl p-4 border border-white/5" data-id="element-258">
                  <p className="text-xs text-slate-400 mb-1" data-id="element-259">Avg Revenue</p>
                  <p className="text-lg font-bold text-emerald-400" data-id="element-260">
                    {formatCurrency(director.avgRevenue)}
                  </p>
                </div>
              </div>

              {/* LO list */}
              <div className="p-6 max-h-[60vh] overflow-y-auto" data-id="element-261">
                <div className="flex items-center justify-between mb-4" data-id="element-262">
                  <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-2" data-id="element-263">
                    <Users className="w-4 h-4 text-indigo-400" data-id="element-264" /> Loan Officers
                  </h3>
                  <span className="text-xs text-slate-500" data-id="element-265">
                    Click an officer to view their loans
                  </span>
                </div>

                {/* LO sortable header */}
                <div className="grid grid-cols-[20px_1fr_80px_120px_120px] gap-3 px-4 py-2 border-b border-white/5 mb-1" data-id="element-266">
                  <span data-id="element-267"></span>
                  <SortHeader label="Officer" field="name" activeField={loSortField} direction={loSortDir} onSort={handleLoSort} data-id="element-268" />
                  <SortHeader label="Loans" field="loansClosed" activeField={loSortField} direction={loSortDir} onSort={handleLoSort} align="right" data-id="element-269" />
                  <SortHeader label="Volume" field="loanVolume" activeField={loSortField} direction={loSortDir} onSort={handleLoSort} align="right" data-id="element-270" />
                  <SortHeader label="Avg Rev / Loan" field="avgRevenue" activeField={loSortField} direction={loSortDir} onSort={handleLoSort} align="right" data-id="element-271" />
                </div>

                <div className="space-y-1.5" data-id="element-272">
                  {sortedLOs.map(lo => <LoanOfficerRow key={lo.id} lo={lo} data-id="element-273" />)}
                </div>
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-white/5 flex items-center justify-end bg-black/10" data-id="element-274">
                <button onClick={onClose} className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white text-sm font-medium transition-colors border border-white/10" data-id="element-275">
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}